import Script from 'next/script';
import { useRouter } from 'next/router';
import { memo, useEffect, useRef, useState } from 'react';
import { RouteData } from '@sitecore-content-sdk/nextjs';
import { PageTypes } from '@generated/Project.GMI.model';
import { isTemplate } from 'lib/utils/sitecore/data-template';
import { usePearCommerceIsEnabled } from 'lib/hooks/usePearCommerceIsEnabled';
import { useSitecorePage } from 'lib/hooks/sitecore/useSitecorePage';
import { useSiteSettings } from 'lib/hooks/useSiteSettings';
import { OneTrustDefaultConsentPreference } from 'components/helpers/OneTrust/OneTrustFilter/OneTrustFilter';
import { findComponent } from 'lib/utils/object-utils';
import { getOneTrustActiveGroups, getOneTrustCookieActiveGroups } from 'lib/utils/onetrust-utils';

// Constants
const PEAR_COMMERCE_BASE_URL = 'https://offers.pearcommerce.com/js/sdk.js';
const PEAR_COMMERCE_SDK_DIR = 'https://offers.pearcommerce.com/js/sdk';
const PEAR_COMMERCE_WIDGETS_URL = 'https://api.pearcommerce.com/v1/vendor-widgets';
const WIDGET_TYPES = {
  RECIPES: 'recipes',
  RETAILER_LIST: 'retailer-list',
  PRODUCT_LOCATOR: 'product-locator',
} as const;
const PEAR_COMMERCE_CONSENT_GROUP = '4';
const LEGACY_PDP_SCRIPT_NAME = 'old-el-paso-pdp';
// Pattern for static per-vendor PDP script slugs (e.g. old-el-paso-pdp)
const PDP_SCRIPT_NAME_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)+$/;

// Types
type WidgetType = (typeof WIDGET_TYPES)[keyof typeof WIDGET_TYPES];

interface ScriptConfig {
  vendorId?: string;
  widget: WidgetType;
  label?: string;
  offerId?: string;
  containerSelector?: string;
  urlSubstringMatch?: string;
}

interface PearWidgetConfig {
  id: string | number;
  offerId?: string | number;
  containerSelector?: string;
  name?: string;
}

interface ResolvedWidgetConfig {
  vendorId: string;
  widgetId: string;
  config: Pick<ScriptConfig, 'offerId' | 'containerSelector'>;
}

const normalizeVariantName = (value?: string): string =>
  (value || '')
    .trim()
    .toLowerCase()
    .replace(/[-_\s]/g, '');

const getPearCommerceWidgetType = (route: RouteData): WidgetType | null => {
  const pearRenderings = findComponent(route, 'PearCommerceProduct');

  if (pearRenderings.length === 0) {
    return null;
  }

  const variant = normalizeVariantName(pearRenderings[0]?.params?.FieldNames as string | undefined);
  if (variant.includes('productlocator')) {
    return WIDGET_TYPES.PRODUCT_LOCATOR;
  }

  return WIDGET_TYPES.RETAILER_LIST;
};

/**
 * Generates the Pear Commerce script URL with the given configuration
 */
export const generateScriptUrl = (config: ScriptConfig): string => {
  const configJson = JSON.stringify(config);
  return `${PEAR_COMMERCE_BASE_URL}?config=${encodeURIComponent(configJson)}`;
};

export const resolvePearWidgetConfig = async (
  vendorId: string,
  widgetId: string,
  pageUrl: string,
  signal?: AbortSignal
): Promise<Pick<ScriptConfig, 'offerId' | 'containerSelector'>> => {
  const url = new URL(PEAR_COMMERCE_WIDGETS_URL);
  url.searchParams.set('domain', new URL(pageUrl).origin);
  url.searchParams.set('url', pageUrl);
  url.searchParams.set('vendorId', vendorId);

  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new Error(`Pear widget configuration request failed with status ${response.status}`);
  }

  const data = (await response.json()) as { widgetConfigs?: PearWidgetConfig[] };
  const widgetConfig = data.widgetConfigs?.find(({ id }) => String(id) === widgetId);
  if (!widgetConfig?.offerId || !widgetConfig.containerSelector) {
    throw new Error(`Pear widget configuration ${widgetId} is incomplete or unavailable`);
  }

  return {
    offerId: String(widgetConfig.offerId),
    containerSelector: widgetConfig.containerSelector,
  };
};

// Finds static per-vendor PDP script slug matching kebab-case naming pattern
export const findVendorPdpScriptName = (
  widgetConfigs: PearWidgetConfig[] | undefined
): string | undefined =>
  widgetConfigs?.find((config) => PDP_SCRIPT_NAME_PATTERN.test(config.name ?? ''))?.name;

// Resolves the vendor's static PDP script slug from vendor-widgets config
export const resolvePearVendorPdpScriptName = async (
  vendorId: string,
  pageUrl: string,
  signal?: AbortSignal
): Promise<string | undefined> => {
  const url = new URL(PEAR_COMMERCE_WIDGETS_URL);
  url.searchParams.set('domain', new URL(pageUrl).origin);
  url.searchParams.set('url', pageUrl);
  url.searchParams.set('vendorId', vendorId);

  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new Error(`Pear widget configuration request failed with status ${response.status}`);
  }

  const data = (await response.json()) as { widgetConfigs?: PearWidgetConfig[] };
  return findVendorPdpScriptName(data.widgetConfigs);
};

/**
 * Determines which widget type should be loaded based on the current route
 */
const getWidgetType = (route: RouteData | null | undefined): WidgetType | null => {
  if (!route) return null;

  if (isTemplate(route, PageTypes.Constants.RecipeWildcardPage.TemplateId)) {
    return WIDGET_TYPES.RECIPES;
  }

  const pearWidgetType = getPearCommerceWidgetType(route);
  if (pearWidgetType) {
    return pearWidgetType;
  }

  if (isTemplate(route, PageTypes.Constants.ProductDetailPage.TemplateId)) {
    return WIDGET_TYPES.RETAILER_LIST;
  }

  return null;
};

/**
 * Shopping component that conditionally loads Pear Commerce scripts
 * based on the current page template type
 */
const Shopping = memo(() => {
  const route = useSitecorePage();
  const router = useRouter();
  const siteSettings = useSiteSettings();
  const requiresExplicitConsent =
    siteSettings?.oneTrustDefaultConsentPreference?.value ===
    OneTrustDefaultConsentPreference.NoConsent;
  const { isEnabled, vendorId, widgetId, isLegacyPdpScriptEnabled } = usePearCommerceIsEnabled();
  const widgetType = getWidgetType(route);
  const productLocatorWidgetId = widgetType === WIDGET_TYPES.PRODUCT_LOCATOR ? widgetId : undefined;
  const [resolvedWidgetConfig, setResolvedWidgetConfig] = useState<ResolvedWidgetConfig>();
  const [hasPearCommerceConsent, setHasPearCommerceConsent] = useState(false);
  const loadedWidgetIdentityRef = useRef<string | null>(null);
  const pdpScriptName = isLegacyPdpScriptEnabled ? LEGACY_PDP_SCRIPT_NAME : undefined;

  useEffect(() => {
    const updateConsent = () => {
      const cookieActiveGroups = getOneTrustCookieActiveGroups();
      // No OptanonConsent cookie means the visitor has not made a choice yet. On sites
      // configured for explicit consent, never fall back to OneTrust's default group
      // state — that is what let Pear drop cookies before the banner was answered.
      if (!cookieActiveGroups && requiresExplicitConsent) {
        setHasPearCommerceConsent(false);
        return;
      }

      const activeGroups = cookieActiveGroups ?? getOneTrustActiveGroups();
      setHasPearCommerceConsent(activeGroups.includes(PEAR_COMMERCE_CONSENT_GROUP));
    };

    updateConsent();
    window.addEventListener('consent.onetrust', updateConsent);

    return () => window.removeEventListener('consent.onetrust', updateConsent);
  }, [requiresExplicitConsent]);

  useEffect(() => {
    if (!hasPearCommerceConsent || !isEnabled || !vendorId || !productLocatorWidgetId) {
      setResolvedWidgetConfig(undefined);
      return;
    }

    const controller = new AbortController();
    resolvePearWidgetConfig(
      vendorId,
      productLocatorWidgetId,
      window.location.href,
      controller.signal
    )
      .then((config) =>
        setResolvedWidgetConfig({ vendorId, widgetId: productLocatorWidgetId, config })
      )
      .catch((error) => {
        if (error instanceof Error && error.name === 'AbortError') return;
        // eslint-disable-next-line no-console
        console.error('[PearCommerce][Shopping] failed to resolve widget configuration', {
          vendorId,
          widgetId: productLocatorWidgetId,
          error,
        });
      });

    return () => controller.abort();
  }, [hasPearCommerceConsent, isEnabled, vendorId, productLocatorWidgetId]);

  const widgetConfig =
    resolvedWidgetConfig &&
    resolvedWidgetConfig.vendorId === vendorId &&
    resolvedWidgetConfig.widgetId === productLocatorWidgetId
      ? resolvedWidgetConfig.config
      : undefined;

  const isReadyToRenderScript =
    hasPearCommerceConsent &&
    isEnabled &&
    Boolean(widgetType) &&
    (!productLocatorWidgetId || Boolean(widgetConfig));
  const widgetIdentity = `${vendorId ?? ''}|${widgetType ?? ''}|${productLocatorWidgetId ?? ''}`;

  useEffect(() => {
    if (!isReadyToRenderScript) return;

    const previousIdentity = loadedWidgetIdentityRef.current;
    if (previousIdentity && previousIdentity !== widgetIdentity) {
      // Force reload when widget identity changes so only one instance runs
      window.location.reload();
      return;
    }

    loadedWidgetIdentityRef.current = widgetIdentity;
  }, [isReadyToRenderScript, widgetIdentity]);

  // Early return if Pear Commerce is not enabled or vendor ID is not available
  if (!hasPearCommerceConsent || !isEnabled) {
    return null;
  }

  // Early return if no widget should be loaded
  if (!widgetType) {
    return null;
  }

  if (productLocatorWidgetId && !widgetConfig) {
    return null;
  }

  const scriptUrl = generateScriptUrl({
    vendorId,
    widget: widgetType,
    // Pear indexes vendor widget configurations by ID and selects that ID through `label`.
    ...(productLocatorWidgetId ? { label: productLocatorWidgetId, ...widgetConfig } : {}),
    // Override urlSubstringMatch with current route for QA and Sitecore editing support
    ...(productLocatorWidgetId ? { urlSubstringMatch: router.asPath } : {}),
  });

  // Key script tag to reset previous instance on config change
  return (
    <>
      <Script key={scriptUrl} src={scriptUrl} strategy="lazyOnload" />
      {pdpScriptName && (
        <Script
          key={`pdp-${pdpScriptName}`}
          src={`${PEAR_COMMERCE_SDK_DIR}/${pdpScriptName}.js`}
          strategy="lazyOnload"
        />
      )}
    </>
  );
});

Shopping.displayName = 'Shopping';

export default Shopping;
